import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import sanityClient from '../lib/sanityClient';

function TeamPage() {
  const [forwards, setForwards] = useState([]);
  const [defense, setDefense] = useState([]);
  const [goalies, setGoalies] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    sanityClient
      .fetch(
        `{
          "players": *[_type == "player" && (active == true || defined(currentSeasonStats.gp) && currentSeasonStats.gp > 0)] {
            _id,
            name,
            number,
            position,
            "imageUrl": image.asset->url,
            slug,
            positionGroup,
            captainStatus,
            active,
            currentSeasonStats
          }
        }`
      )
      .then((data) => {
        // Process players
        const processedPlayers = data.players.map((player) => ({
          ...player,
          // Ensure imageUrl is set or undefined (not null)
          imageUrl: player.imageUrl || undefined,
          // Ensure captainStatus is set (default to 'none')
          captainStatus: player.captainStatus || 'none',
        }));

        // Sort players by position group
        const forwardsList = [];
        const defenseList = [];
        const goaliesList = [];

        processedPlayers.forEach((player) => {
          if (player.positionGroup === 'Forward') {
            forwardsList.push(player);
          } else if (player.positionGroup === 'Defense') {
            defenseList.push(player);
          } else if (player.positionGroup === 'Goalie') {
            goaliesList.push(player);
          }
        });

        // Sort by number within each position group
        const sortByNumber = (a, b) => {
          // First sort by captain status (captain first, then associate captains, then others)
          if (a.captainStatus !== b.captainStatus) {
            if (a.captainStatus === 'captain') return -1;
            if (b.captainStatus === 'captain') return 1;
            if (a.captainStatus === 'associate') return -1;
            if (b.captainStatus === 'associate') return 1;
          }

          // Then sort by jersey number
          const numA = parseInt(a.number, 10) || 0;
          const numB = parseInt(b.number, 10) || 0;
          return numA - numB;
        };

        setForwards(forwardsList.sort(sortByNumber));
        setDefense(defenseList.sort(sortByNumber));
        setGoalies(goaliesList.sort(sortByNumber));

        setIsLoading(false);
      })
      .catch((err) => {
        console.error('Error fetching team roster:', err);
        setError('Failed to load team roster. Please try again later.');
        setIsLoading(false);
      });
  }, []);

  if (isLoading) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="text-white text-xl">Loading team roster...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-black min-h-screen flex justify-center items-center">
        <div className="bg-gray-900 p-6 rounded-lg text-white text-xl">{error}</div>
      </div>
    );
  }

  return (
    <div className="bg-black min-h-screen py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-white">Team Roster</h1>
          <Link
            to="/coaches"
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-md font-medium inline-flex items-center"
          >
            View Coaching Staff
          </Link>
        </div>

        {forwards.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Forwards</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {forwards.map((player) => (
                <Link key={player._id} to={`/players/${player.slug.current}`} className="block">
                  <div
                    className={`bg-gray-900 rounded-lg overflow-hidden border transition-all relative ${
                      player.active
                        ? 'border-gray-800 hover:border-yellow-600'
                        : 'border-red-600/50 hover:border-red-500 opacity-75'
                    }`}
                  >
                    {/* Inactive Badge */}
                    {!player.active && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
                        INACTIVE
                      </div>
                    )}

                    {/* Captain Badge */}
                    {player.captainStatus === 'captain' && (
                      <div className="absolute top-2 right-2 bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                        C
                      </div>
                    )}
                    {player.captainStatus === 'associate' && (
                      <div className="absolute top-2 right-2 bg-gray-700 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                        A
                      </div>
                    )}

                    <div className="aspect-square bg-gray-800 flex items-center justify-center">
                      <img
                        src={player.imageUrl || '/player-placeholder.png'}
                        alt={player.name}
                        className="w-full h-full object-contain bg-gray-800"
                        onError={(e) => {
                          // Fallback if image fails to load and set object-contain for better fitting
                          e.currentTarget.src = '/player-placeholder.png';
                          e.currentTarget.classList.remove('object-cover');
                          e.currentTarget.classList.add('object-contain', 'p-4');
                        }}
                      />
                    </div>
                    <div className="p-4 text-center">
                      <div className="text-yellow-500 font-bold mb-1">#{player.number}</div>
                      <div className="text-white font-medium flex items-center justify-center">
                        {player.name}
                        {player.captainStatus === 'captain' && (
                          <span className="ml-1 text-yellow-500 font-bold">(C)</span>
                        )}
                        {player.captainStatus === 'associate' && (
                          <span className="ml-1 text-gray-400 font-bold">(A)</span>
                        )}
                      </div>
                      <div className="text-gray-400 text-sm">{player.position}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {defense.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Defense</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {defense.map((player) => (
                <Link key={player._id} to={`/players/${player.slug.current}`} className="block">
                  <div
                    className={`bg-gray-900 rounded-lg overflow-hidden border transition-all relative ${
                      player.active
                        ? 'border-gray-800 hover:border-yellow-600'
                        : 'border-red-600/50 hover:border-red-500 opacity-75'
                    }`}
                  >
                    {/* Inactive Badge */}
                    {!player.active && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
                        INACTIVE
                      </div>
                    )}

                    {/* Captain Badge */}
                    {player.captainStatus === 'captain' && (
                      <div className="absolute top-2 right-2 bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                        C
                      </div>
                    )}
                    {player.captainStatus === 'associate' && (
                      <div className="absolute top-2 right-2 bg-gray-700 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                        A
                      </div>
                    )}

                    <div className="aspect-square bg-gray-800 flex items-center justify-center">
                      <img
                        src={player.imageUrl || '/player-placeholder.png'}
                        alt={player.name}
                        className="w-full h-full object-contain bg-gray-800"
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.currentTarget.src = '/player-placeholder.png';
                          e.currentTarget.classList.remove('object-cover');
                          e.currentTarget.classList.add('object-contain', 'p-4');
                        }}
                      />
                    </div>
                    <div className="p-4 text-center">
                      <div className="text-yellow-500 font-bold mb-1">#{player.number}</div>
                      <div className="text-white font-medium flex items-center justify-center">
                        {player.name}
                        {player.captainStatus === 'captain' && (
                          <span className="ml-1 text-yellow-500 font-bold">(C)</span>
                        )}
                        {player.captainStatus === 'associate' && (
                          <span className="ml-1 text-gray-400 font-bold">(A)</span>
                        )}
                      </div>
                      <div className="text-gray-400 text-sm">{player.position}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {goalies.length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-white mb-6">Goalies</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
              {goalies.map((player) => (
                <Link key={player._id} to={`/players/${player.slug.current}`} className="block">
                  <div
                    className={`bg-gray-900 rounded-lg overflow-hidden border transition-all relative ${
                      player.active
                        ? 'border-gray-800 hover:border-yellow-600'
                        : 'border-red-600/50 hover:border-red-500 opacity-75'
                    }`}
                  >
                    {/* Inactive Badge */}
                    {!player.active && (
                      <div className="absolute top-2 left-2 bg-red-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md">
                        INACTIVE
                      </div>
                    )}

                    {/* Captain Badge */}
                    {player.captainStatus === 'captain' && (
                      <div className="absolute top-2 right-2 bg-yellow-600 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                        C
                      </div>
                    )}
                    {player.captainStatus === 'associate' && (
                      <div className="absolute top-2 right-2 bg-gray-700 text-white text-xs font-bold px-2 py-1 rounded shadow-md z-10">
                        A
                      </div>
                    )}

                    <div className="aspect-square bg-gray-800 flex items-center justify-center">
                      <img
                        src={player.imageUrl || '/player-placeholder.png'}
                        alt={player.name}
                        className="w-full h-full object-contain bg-gray-800"
                        onError={(e) => {
                          // Fallback if image fails to load
                          e.currentTarget.src = '/player-placeholder.png';
                          e.currentTarget.classList.remove('object-cover');
                          e.currentTarget.classList.add('object-contain', 'p-4');
                        }}
                      />
                    </div>
                    <div className="p-4 text-center">
                      <div className="text-yellow-500 font-bold mb-1">#{player.number}</div>
                      <div className="text-white font-medium flex items-center justify-center">
                        {player.name}
                        {player.captainStatus === 'captain' && (
                          <span className="ml-1 text-yellow-500 font-bold">(C)</span>
                        )}
                        {player.captainStatus === 'associate' && (
                          <span className="ml-1 text-gray-400 font-bold">(A)</span>
                        )}
                      </div>
                      <div className="text-gray-400 text-sm">{player.position}</div>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TeamPage;
